import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';

export const propertyService = {
  // Create new property
  async createProperty(propertyData) {
    try {
      const docRef = await addDoc(collection(db, 'properties'), {
        ...propertyData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get property by ID
  async getProperty(propertyId) {
    try {
      const propertyDoc = await getDoc(doc(db, 'properties', propertyId));
      if (propertyDoc.exists()) {
        return { id: propertyDoc.id, ...propertyDoc.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Subscribe to property by ID
  subscribeToPropertyById(propertyId, callback) {
    try {
      const propertyRef = doc(db, 'properties', propertyId);
      return onSnapshot(propertyRef, (doc) => {
        if (doc.exists()) {
          callback({ id: doc.id, ...doc.data() });
        } else {
          callback(null);
        }
      });
    } catch (error) {
      console.error('Error subscribing to property:', error);
      callback(null);
    }
  },

  // Update property
  async updateProperty(propertyId, updates) {
    try {
      const propertyRef = doc(db, 'properties', propertyId);
      await updateDoc(propertyRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      throw error;
    }
  },

  // Delete property
  async deleteProperty(propertyId) {
    try {
      await deleteDoc(doc(db, 'properties', propertyId));
    } catch (error) {
      throw error;
    }
  },

  // Get all properties (Admin function)
  async getAllProperties() {
    try {
      const propertiesCollection = collection(db, 'properties');
      const propertiesSnapshot = await getDocs(propertiesCollection);
      const properties = [];
      
      propertiesSnapshot.forEach((doc) => {
        properties.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return properties;
    } catch (error) {
      console.error('Error getting all properties:', error);
      throw error;
    }
  },

  // Get properties by owner ID
  async getPropertiesByOwner(ownerId) {
    try {
      const q = query(
        collection(db, 'properties'),
        where('ownerId', '==', ownerId)
        // Temporarily removed orderBy to avoid index requirement
      );
      const querySnapshot = await getDocs(q);
      const properties = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort properties client-side for now
      properties.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      return properties;
    } catch (error) {
      throw error;
    }
  },

  // Get properties by tenant ID (properties where tenant is assigned)
  async getPropertiesByTenantAssignments(tenantId) {
    try {
      // First get tenant assignments
      const tenantService = await import('./tenantService.js');
      const assignments = await tenantService.tenantService.getTenantAssignmentsByTenant(tenantId);
      
      // Then get the properties for those assignments
      const properties = [];
      for (const assignment of assignments) {
        const property = await this.getProperty(assignment.propertyId);
        if (property) {
          properties.push(property);
        }
      }
      
      return properties;
    } catch (error) {
      throw error;
    }
  },

  // Get properties by tenant ID (for tenants to see their rental property)
  async getPropertiesByTenant(tenantId) {
    try {
      const q = query(
        collection(db, 'properties'),
        where('tenantId', '==', tenantId)
        // Temporarily removed orderBy to avoid index requirement
      );
      const querySnapshot = await getDocs(q);
      const properties = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort properties client-side for now
      properties.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      return properties;
    } catch (error) {
      throw error;
    }
  },

  // Listen to properties by owner in real-time
  subscribeToPropertiesByOwner(ownerId, callback) {
    try {
      const q = query(
        collection(db, 'properties'),
        where('ownerId', '==', ownerId)
        // Temporarily removed orderBy to avoid index requirement
      );

      return onSnapshot(q, (querySnapshot) => {
        const properties = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort properties client-side for now
        properties.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA; // Descending order
        });
        callback(properties);
      }, (error) => {
        console.error('Error in property subscription:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up property subscription:', error);
      callback([]);
      return () => {};
    }
  },

  // Listen to properties by tenant in real-time
  subscribeToPropertiesByTenant(tenantId, callback) {
    try {
      // This is a more complex subscription that needs to watch tenant assignments
      // For now, we'll use a polling approach or implement a more sophisticated solution
      const loadProperties = async () => {
        try {
          const properties = await this.getPropertiesByTenant(tenantId);
          callback(properties);
        } catch (error) {
          console.error('Error loading tenant properties:', error);
          callback([]);
        }
      };

      // Load initially
      loadProperties();

      // Set up interval to check for changes (every 30 seconds)
      const interval = setInterval(loadProperties, 30000);

      return () => {
        clearInterval(interval);
      };
    } catch (error) {
      console.error('Error setting up tenant property subscription:', error);
      callback([]);
      return () => {};
    }
  },

  // Get property statistics
  async getPropertyStats(ownerId) {
    try {
      const properties = await this.getPropertiesByOwner(ownerId);
      return {
        totalProperties: properties.length,
        residentialProperties: properties.filter(p => p.type === 'residential').length,
        commercialProperties: properties.filter(p => p.type === 'commercial').length,
        industrialProperties: properties.filter(p => p.type === 'industrial').length,
        occupiedProperties: properties.filter(p => p.status === 'occupied').length,
        vacantProperties: properties.filter(p => p.status === 'vacant').length
      };
    } catch (error) {
      throw error;
    }
  }
}; 